/**
 * cart-addon.js — Ari Service Add-ons
 * Gift wrap / framing quantity follows parent cart line quantity.
 * Services cascade-remove when parent line is removed.
 */

import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

(function () {
  'use strict';

  const inFlight = new Set();

  function collectCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  function withSectionsBody(base) {
    const sectionIds = collectCartSectionIds();
    if (sectionIds.length) {
      base.sections = sectionIds.join(',');
      base.sections_url = window.location.pathname;
    }
    return JSON.stringify(base);
  }

  /** @param {Record<string, unknown> | null | undefined} parsed */
  async function morphCartSectionsIfPresent(parsed) {
    if (!parsed || typeof parsed !== 'object' || !parsed.sections) return;
    const sections = /** @type {Record<string, string>} */ (parsed.sections);
    const ids = collectCartSectionIds();
    for (const id of ids) {
      const html = sections[id];
      if (html) await morphSection(id, html);
    }
  }

  /** True when every cart-items section id has non-empty HTML in the API payload */
  function sectionsPayloadComplete(parsed) {
    const ids = collectCartSectionIds();
    if (!ids.length) return true;
    if (!parsed || typeof parsed !== 'object' || !parsed.sections) return false;
    const sec = /** @type {Record<string, unknown>} */ (parsed.sections);
    return ids.every((id) => {
      const html = sec[id];
      return typeof html === 'string' && html.length > 0;
    });
  }

  /** Morph from cart AJAX `sections` when present; otherwise re-fetch sections (add.js often omits sections for JSON). */
  async function morphOrRerenderCartSections(parsed) {
    await morphCartSectionsIfPresent(parsed);
    if (!sectionsPayloadComplete(parsed)) {
      const ids = collectCartSectionIds();
      await Promise.all(ids.map((id) => sectionRenderer.renderSection(id, { cache: false })));
    }
  }

  function buildUpdateBody(updates) {
    const sectionIds = collectCartSectionIds();
    const body = /** @type {Record<string, unknown>} */ ({ updates });
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  /** @param {unknown} data */
  function isSuccessfulCartPayload(data) {
    return Boolean(
      data &&
        typeof data === 'object' &&
        Array.isArray(/** @type {Record<string, unknown>} */ (data).items)
    );
  }

  /** @param {unknown} parsed */
  function cartChangeFailed(parsed) {
    if (!parsed || typeof parsed !== 'object') return true;
    const p = /** @type {Record<string, unknown>} */ (parsed);
    if (p.errors) return true;
    return !Array.isArray(p.items);
  }

  /** Shopify cart/add.js: truthy `status` means error (same as product-form). */
  function cartAddFailed(parsed) {
    if (!parsed || typeof parsed !== 'object') return true;
    const p = /** @type {Record<string, unknown>} */ (parsed);
    if (p.status) return true;
    if (p.errors) return true;
    return false;
  }

  // ── 1. PAGE-LOAD: header / sticky totals only (markup comes from Liquid) ──
  document.addEventListener('DOMContentLoaded', function () {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => refreshCartTotals(cart))
      .catch(err => console.warn('Ari add-ons: cart refresh on load failed', err));
  });

  // ── 2. EXPAND / COLLAPSE ──────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const toggleBtn = e.target.closest('.ari-service-block__toggle');
    if (!toggleBtn) return;

    let desc = toggleBtn.previousElementSibling;
    if (!desc || !desc.classList.contains('ari-service-block__description')) {
      const descId = toggleBtn.getAttribute('aria-controls');
      desc = descId ? document.getElementById(descId) : null;
    }
    if (!desc) return;

    e.preventDefault();
    e.stopPropagation();
    const isCurrentlyExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';

    if (isCurrentlyExpanded) {
      toggleBtn.setAttribute('aria-expanded', 'false');
      desc.setAttribute('hidden', '');
      desc.style.display = 'none';
      const label = toggleBtn.querySelector('.ari-toggle-label');
      if (label) label.textContent = 'อ่านรายละเอียด';
      const arrow = toggleBtn.querySelector('.ari-toggle-arrow');
      if (arrow) arrow.classList.remove('ari-toggle-arrow--expanded');
    } else {
      toggleBtn.setAttribute('aria-expanded', 'true');
      desc.removeAttribute('hidden');
      desc.style.display = '';
      const label = toggleBtn.querySelector('.ari-toggle-label');
      if (label) label.textContent = 'ย่อรายละเอียด';
      const arrow = toggleBtn.querySelector('.ari-toggle-arrow');
      if (arrow) arrow.classList.add('ari-toggle-arrow--expanded');
    }
  }, true);

  // ── 3. เพิ่ม — add service with same qty as parent line ─────────────────────
  document.addEventListener('click', function (e) {
    const addBtn = e.target.closest('.ari-service-block__add');
    if (!addBtn) return;

    const serviceId   = addBtn.dataset.serviceId;
    const variantId   = parseInt(addBtn.dataset.variantId, 10);
    const itemKey     = addBtn.dataset.itemKey;
    const itemId     = addBtn.dataset.itemId;
    const serviceType = addBtn.dataset.serviceType;
    if (inFlight.has(serviceId)) return;
    addFirstServiceUnit(variantId, itemKey, serviceType, serviceId, addBtn, itemId);
  });

  function addFirstServiceUnit(variantId, itemKey, serviceType, serviceId, btn, itemId) {
    inFlight.add(serviceId);
    btn.disabled = true;

    fetchCart()
      .then((cart) => {
        const parent = cart.items.find((i) => i.key === itemKey);
        const parentQty = Math.max(1, Number(parent?.quantity || 1));
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: withSectionsBody({
            id: variantId,
            quantity: parentQty,
            properties: {
              _for_item_key: itemKey,
              _for_item_id: 'gid://shopify/CartLine/' + itemId,
              _service_type: serviceType,
              _service_id: serviceId,
            },
          }),
        }).then((r) => r.json());
      })
      .then(async (data) => {
        if (cartAddFailed(data)) {
          throw new Error(
            /** @type {{ description?: string; message?: string }} */ (data).description ||
              /** @type {{ message?: string }} */ (data).message ||
              'Add service failed'
          );
        }
        await morphOrRerenderCartSections(data);
        refreshCartTotals(data);
      })
      .catch(err => {
        console.error('Ari add-ons: add service failed', err);
      })
      .finally(() => {
        btn.disabled = false;
        inFlight.delete(serviceId);
      });
  }

  // ── 4. ลบ — remove service line ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const delBtn = e.target.closest('[data-ari-service-delete]');
    if (!delBtn) return;

    const serviceId = delBtn.dataset.serviceId;
    const lineKey   = delBtn.dataset.serviceLineKey;
    if (!serviceId || !lineKey || inFlight.has(serviceId)) return;

    inFlight.add(serviceId);
    delBtn.disabled = true;

    fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: withSectionsBody({ id: lineKey, quantity: 0 }),
    })
      .then(r => r.json())
      .then(async (data) => {
        if (cartChangeFailed(data)) throw new Error(data.errors || 'Remove failed');
        await morphOrRerenderCartSections(data);
        refreshCartTotals(data);
      })
      .catch(err => {
        console.error('Ari add-ons: remove service failed', err);
      })
      .finally(() => {
        delBtn.disabled = false;
        inFlight.delete(serviceId);
      });
  });

  function fetchCart() {
    return fetch('/cart.js').then(r => r.json());
  }

  /** After any cart change, align gift wrap / framing lines to parent qty (`_for_item_key`). */
  let serviceQtySyncTimer = null;
  let serviceQtySyncRunning = false;

  async function syncServiceLinesToParentQuantities() {
    if (serviceQtySyncRunning) return;
    serviceQtySyncRunning = true;
    try {
      const cart = await fetchCart();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

      const updates = {};
      for (const line of cart.items) {
        const props = line.properties;
        const forKey = props && props._for_item_key != null ? String(props._for_item_key) : '';
        if (!forKey) continue;

        const parent = cart.items.find((i) => i.key === forKey);
        if (!parent) continue;

        const targetQty = Math.max(0, Number(parent.quantity || 0));
        if (targetQty < 1) continue;

        if (Number(line.quantity) !== targetQty) {
          updates[line.key] = targetQty;
        }
      }

      if (Object.keys(updates).length === 0) return;

      const data = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildUpdateBody(updates)),
      }).then((r) => r.json());

      if (!isSuccessfulCartPayload(data)) {
        console.warn('Ari add-ons: sync service qty failed', data);
        return;
      }
      await morphOrRerenderCartSections(data);
      refreshCartTotals(data);
    } catch (err) {
      console.warn('Ari add-ons: sync service qty error', err);
    } finally {
      serviceQtySyncRunning = false;
    }
  }

  document.addEventListener(ThemeEvents.cartUpdate, function () {
    clearTimeout(serviceQtySyncTimer);
    serviceQtySyncTimer = window.setTimeout(function () {
      void syncServiceLinesToParentQuantities();
    }, 320);
  });

  document.addEventListener('DOMContentLoaded', function () {
    if (!document.querySelector('cart-items-component')) return;
    window.setTimeout(function () {
      void syncServiceLinesToParentQuantities();
    }, 500);
  });

  // ── 5. CASCADE REMOVE (parent line delete) ────────────────────────────────
  // After cascade, morph can replace the cart section — the original remove button
  // is detached. A synthetic click then targets a stale subtree / wrong component
  // and `onLineItemRemove(line)` uses a line index that no longer matches the cart.
  // Remove the parent line by stable line-item `id` (key) via /cart/change.js instead
  // of delegating to the theme, then morph like the theme would.
  window.addEventListener(
    'click',
    async function (e) {
      if (e._ariHandled) return;

      const removeBtn = e.target.closest('[data-remove-cart-item]');
      if (!removeBtn) return;

      const cartItemEl     = removeBtn.closest('[data-cart-item-key]');
      const removedItemKey = cartItemEl?.dataset.cartItemKey;
      if (!removedItemKey) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      try {
        await cascadeRemoveServicesForItem(removedItemKey);
      } catch (err) {
        console.warn('Ari add-ons: cascade remove failed', err);
      }

      try {
        const data = await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: withSectionsBody({ id: removedItemKey, quantity: 0 }),
        }).then(r => r.json());

        if (cartChangeFailed(data)) {
          throw new Error(typeof data.errors === 'string' ? data.errors : 'Remove line failed');
        }
        await morphOrRerenderCartSections(data);
        refreshCartTotals(data);
      } catch (err) {
        console.error('Ari add-ons: remove cart line failed', err);
      }
    },
    true
  );

  async function cascadeRemoveServicesForItem(removedItemKey) {
    const cart = await fetchCart();

    const rm = String(removedItemKey);
    const serviceItems = cart.items.filter(
      item => item.properties && String(item.properties['_for_item_key']) === rm
    );

    if (serviceItems.length === 0) return;

    const updates = {};
    serviceItems.forEach(item => { updates[item.key] = 0; });

    const updatedCart = await fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildUpdateBody(updates))
    }).then(r => r.json());

    if (!isSuccessfulCartPayload(updatedCart)) {
      console.warn('Ari add-ons: cascade remove cart/update failed', updatedCart);
      return;
    }

    await morphOrRerenderCartSections(updatedCart);
    refreshCartTotals(updatedCart);
  }

  // ── 6. HELPERS ───────────────────────────────────────────────────────────

  function formatStickyTotalTHB(totalCents) {
    const totalTHB = totalCents / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(totalTHB);
    return `${formatted} B.`;
  }

  function syncHeaderCartBubble(itemCount) {
    document.querySelectorAll('cart-icon').forEach(function (el) {
      if (typeof el.renderCartBubble === 'function') {
        el.renderCartBubble(itemCount, false, false);
      }
    });
  }

  function refreshCartTotals(cart) {
    const update = (cartData) => {
      const stickyTotal = document.querySelector('[data-cart-total]');
      if (stickyTotal && cartData.total_price !== undefined) {
        stickyTotal.textContent = formatStickyTotalTHB(cartData.total_price);
      }

      const countEl = document.querySelector('[data-cart-count]');
      if (countEl && typeof cartData.item_count === 'number') {
        countEl.textContent = cartData.item_count;
      }

      if (typeof cartData.item_count === 'number') {
        syncHeaderCartBubble(cartData.item_count);
      }
    };

    if (cart && cart.total_price !== undefined) {
      update(cart);
    } else {
      fetchCart().then(update).catch(err => console.warn('Ari add-ons: total refresh failed', err));
    }
  }

})();
